import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, MapPin, User } from 'lucide-react';
import { contentApi } from '../../services/contentApi';
import { EventDetail } from '../../types/content';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import { PageLoading } from '../../components/UI/LoadingOverlay';
import CourseFee from '../../components/UI/CourseFee';
import { useAuth } from '../../context/AuthContext';
import { formatEventWhen, speakerHeadline } from '../../utils/eventDisplay';
import { COURSE_IMAGES } from '../../config/courseImages';

const EventDetailPage: React.FC = () => {
  const { slug } = useParams();
  const { isAuthenticated } = useAuth();
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const reload = () => {
    if (!slug) return;
    contentApi.getEventBySlug(slug).then((r) => setEvent(r.data.event)).catch(() => setError('This event is not available.'));
  };

  useEffect(() => { reload(); }, [slug]);

  const enroll = async () => {
    if (!event) return;
    setSaving(true);
    setError(null);
    try {
      await contentApi.enrollEvent(event.id);
      setMessage('Enrollment submitted. An admin will review your request.');
      reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unable to enroll.');
    } finally {
      setSaving(false);
    }
  };

  const pay = async () => {
    if (!event?.enrollment) return;
    setSaving(true);
    try {
      await contentApi.payEventEnrollment(event.enrollment.id);
      setMessage('Payment recorded. Your event access is now active.');
      reload();
    } finally {
      setSaving(false);
    }
  };

  if (error && !event) {
    return <div className="max-w-3xl mx-auto px-4 py-16 p-3 rounded-md bg-error-50 text-error-600 border border-error-200">{error}</div>;
  }
  if (!event) return <PageLoading label="Loading event…" />;

  const enrollment = event.enrollment;
  const status = enrollment?.status;
  const speaker = speakerHeadline(event);

  return (
    <div>
      <section className="page-intro">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <Link to="/events" className="text-sm font-medium text-primary-700 hover:text-primary-800">← All events</Link>
          <img
            src={event.coverImageUrl || COURSE_IMAGES.eventsHero}
            alt=""
            className="mt-5 h-48 sm:h-64 w-full max-w-3xl object-cover rounded-2xl bg-gray-100"
          />
          <p className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">{event.mode} event</p>
          <h1 className="mt-3 text-4xl md:text-5xl font-bold text-gray-900 tracking-tight leading-tight">{event.title}</h1>
          {event.subtitle && <p className="mt-4 text-xl text-gray-600">{event.subtitle}</p>}
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <div className="surface-card p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500 inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /> Date & time</p>
              <p className="mt-2 font-medium text-gray-900">{formatEventWhen(event.startsAt, event.endsAt)}</p>
            </div>
            <div className="surface-card p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500 inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> Location</p>
              <p className="mt-2 font-medium text-gray-900">{event.location || (event.mode === 'Online' ? 'Online' : 'Location announced later')}</p>
            </div>
            {speaker && (
              <div className="surface-card p-4 sm:col-span-2">
                <p className="text-xs uppercase tracking-wide text-gray-500 inline-flex items-center gap-1.5"><User className="h-4 w-4" /> Speaker</p>
                <p className="mt-2 text-lg font-semibold text-gray-900">{event.speakerName}</p>
                {(event.speakerTitle || event.speakerCompany) && (
                  <p className="mt-1 text-gray-600">{[event.speakerTitle, event.speakerCompany].filter(Boolean).join(' · ')}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {event.summary && <p className="text-lg text-gray-700 leading-relaxed max-w-3xl">{event.summary}</p>}
        <p className="mt-6 text-base font-semibold text-gray-900"><CourseFee fee={Number(event.price || 0)} discount={0} /></p>

        {message && <p className="mt-6 p-3 rounded-md bg-primary-50 text-primary-800 border border-primary-200">{message}</p>}
        {error && event && <p className="mt-6 p-3 rounded-md bg-error-50 text-error-600 border border-error-200">{error}</p>}

        <Card className="mt-8 p-6">
          {!isAuthenticated && (
            <div>
              <p className="text-gray-700">Sign in to request enrollment. Online meeting links are shown only after approval.</p>
              <Link to="/login" className="mt-4 inline-flex"><Button>Sign in to enroll</Button></Link>
            </div>
          )}
          {isAuthenticated && !enrollment && (
            <div>
              <p className="text-gray-700">Enrollment is reviewed by an admin before it is confirmed.</p>
              <Button className="mt-4" isLoading={saving} onClick={enroll}>Request enrollment</Button>
            </div>
          )}
          {status === 'Pending' && <p className="text-gray-700">Your request is pending admin approval.</p>}
          {status === 'Rejected' && <p className="text-error-700">This enrollment was not approved.</p>}
          {status === 'PendingPayment' && (
            <div>
              <p className="text-gray-700">Approved. Complete payment to confirm your seat.</p>
              <Button className="mt-4" isLoading={saving} onClick={pay}>Complete payment</Button>
            </div>
          )}
          {status === 'Approved' && (
            <div>
              <p className="font-medium text-gray-900">You are enrolled.</p>
              {event.mode === 'Online' && event.canSeeMeetingLink && event.meetingLink ? (
                <a href={event.meetingLink} target="_blank" rel="noreferrer" className="mt-4 inline-flex"><Button>Join online</Button></a>
              ) : event.mode === 'Online' ? (
                <p className="mt-2 text-sm text-gray-600">The meeting link will appear here once the admin adds it.</p>
              ) : (
                <p className="mt-2 text-sm text-gray-600">{event.location}</p>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default EventDetailPage;
